import { DataDeadZone, postMagnetKeyInfo, ResDeviceState } from '@libs/types/response'
import { DeviceProtocol } from '../device/device_model'
import type { DeviceOnboardConfigList } from '../define/response'
import { dataProfileManage } from '@hid/snapshot/profile'
import { HuoChaiRenDevices, KK, Layout, RoYuanDevice, SetMagnetSenior, VK } from '@hid/define/define'
import { HIDFromVk, VKFromHID } from '@hid/define/define_fn'
import { fakeSocket } from '@hid/fakesocket/fakesocket'
import { DriverSocketMsgType } from '@libs/types/message'
import type { FnFactoryResetProvider } from './fn_factory_reset'
import { deviceManager } from '@hid/device/device_manager'
import { delay } from '@libs/utils/tools'
import { Fe87v2DeviceList, He6UltraDeviceList, SlefDevMagnetList } from '@/define/device_vendor'

/**
 * 板载切换监控的统一抑制时长（ms）。
 * 任何驱动主动 setOnboardConfig / reset / 整包同步前都应通过 suppressOnboardConfigMonitor()
 * 抑制 monitor，避免自身写流量被识别为"硬件主动切换"产生幽灵 Start/End。
 *
 * - Default: 默认抑制（仅一次写命令）
 * - Reset: 设备 reset 后等待固件回稳
 * - SyncSequence: 覆盖一次 setOnboardConfig + 整包 sync
 * - LongSyncSequence: 覆盖跨槽逐个下发（如复制切换键到所有槽位）
 */
export const OnboardMonitorSuppress = {
  Default: 800,
  Reset: 1500,
  SyncSequence: 5000,
  LongSyncSequence: 10000,
} as const

export abstract class MagnetDriver {
  abstract startCalibration(): Promise<void>
  abstract endCalibration(): Promise<void>

  abstract startTravels(): Promise<void>
  abstract endTravels(): Promise<void>

  abstract syncKeysTravels(): Promise<void>

  abstract syncSeniorkey(): Promise<void>

  abstract getKeysTravels(keys?: KK[]): Promise<postMagnetKeyInfo[]>

  abstract readOrSetDeadZone(isWrite: boolean, value?: [number, number]): Promise<[number, number]>

  abstract getKeysDeadZone(keys?: KK[]): Promise<Map<KK, DataDeadZone>>

  abstract syncKeysDeadZone(): Promise<void>

  async getSeniorKeyLayoutOverlay(): Promise<Map<KK, SetMagnetSenior> | undefined> {
    return undefined
  }

  abstract DemoDebug(): Promise<void>

  abstract destroy(): void
}

export abstract class BaseDriver {
  protected isWireless: boolean = false
  magnet: MagnetDriver | undefined
  private onboardConfigMonitorDispose: (() => void) | null = null
  private onboardConfigMonitorTimer: ReturnType<typeof setTimeout> | null = null
  private onboardConfigLastKnownIndex: number | null = null
  private onboardConfigMonitorMutedUntil = 0

  constructor(protected DeviceHID: DeviceProtocol) {
    const fake = (this.DeviceHID as any).deviceHID.device as HIDDevice
    const isWireless = fake.collections.some((c) => c.usagePage === 17)
    this.isWireless = isWireless
  }

  public changeProfileFlag = false

  setChangeProfileFlag(flag: boolean) {
    this.changeProfileFlag = flag
  }

  get save() {
    return dataProfileManage().dataProfile
  }
  get resetKeys() {
    return dataProfileManage().ResetKeys
  }
  get resetKmeKeys() {
    return dataProfileManage().ResetKmeKeys
  }

  changeDpi(index: number) {
    dataProfileManage().changeDpi(index)
  }
  changeReportRate(value: number) {
    dataProfileManage().changeMouseReportRate(value)
  }

  clearKmeKeys() {
    dataProfileManage().clearKmeKeys()
  }

  abstract getVersion(): Promise<string>
  abstract syncCustomColor(): Promise<void>

  /**
   * 自定义灯光是否作为独立覆盖层叠加在普通灯效之上。
   * 返回 true 的设备：自定义颜色不会被下发普通灯效自动覆盖，切换到非自定义配置时
   * 必须重新下发（空的）自定义颜色以清空硬件残留的覆盖层，否则上一个配置的自定义
   * 灯光会与新配置的普通灯效同时显示。
   * 默认 false：多数设备自定义与普通灯效互斥，下发普通灯效即完成覆盖。
   */
  async customLightOverlaysNormal(): Promise<boolean> {
    return false
  }

  abstract syncLeds(): Promise<void>
  abstract syncKeys(): Promise<void>
  abstract syncMacros(): Promise<void>
  abstract syncConfig(): Promise<void>
  abstract getDeviceState(): Promise<ResDeviceState>
  abstract reset(): Promise<void>
  abstract setOrReadSpecificItem(item: string, isWrite: boolean, value: any): Promise<any>
  abstract pair24g(): Promise<number>
  abstract updateFirmware(data: number[]): Promise<boolean>

  abstract syncDecorationLed(): Promise<void>

  abstract syncDynamicColor(): Promise<boolean>

  async syncImageLightFrame(_data: number[]): Promise<boolean> {
    return false
  }

  getSupportedDeviceFunctionKeys(): VK[] {
    return []
  }

  supportsOnboardConfigs(): boolean {
    return false
  }

  supportsOnboardConfigRename(): boolean {
    return false
  }

  async getOnboardConfigList(): Promise<DeviceOnboardConfigList> {
    throw new Error('onboard config not supported')
  }

  async getCurrentOnboardConfigIndex(): Promise<number> {
    throw new Error('onboard config not supported')
  }

  async setOnboardConfig(_index: number): Promise<number> {
    throw new Error('onboard config not supported')
  }

  async setOnboardConfigName(_index: number, _name: string): Promise<void> {
    throw new Error('onboard config not supported')
  }

  /**
   * 当前已绑定到硬件槽位的配置索引（来自本地 profile 的 slotIndex）。
   * 子类驱动在 ensureBoundOnboardConfig 等场景中复用，避免各自实现一遍。
   */
  protected getBoundOnboardConfigIndex(): number | null {
    const cfgList = dataProfileManage().CfgList()
    const currentProfile = cfgList.List[cfgList.Current]
    return typeof currentProfile?.slotIndex === 'number' ? currentProfile.slotIndex : typeof currentProfile?.OnboardSlot === 'number' ? currentProfile.OnboardSlot : null
  }

  /**
   * 写设备前矫正：若硬件当前激活槽位与本地绑定槽位不一致，强制把硬件切回绑定槽位。
   * 默认实现适用于绝大多数支持板载配置的设备（na87 / jingtai_v2 等）。
   * 若设备需要额外的就绪/版本校验（如 huochairen），子类应当 override。
   */
  async ensureBoundOnboardConfig(force: boolean = false): Promise<number | null> {
    if (!this.supportsOnboardConfigs()) {
      return null
    }

    const boundIndex = this.getBoundOnboardConfigIndex()
    if (boundIndex === null) {
      return null
    }

    if (!force) {
      try {
        const currentIndex = await this.getCurrentOnboardConfigIndex()
        this.rememberOnboardConfigIndex(currentIndex)
        if (currentIndex === boundIndex) {
          return currentIndex
        }
      } catch {
        // Fall through to reassert the bound onboard slot.
      }
    }

    // 使用较长的抑制时间，覆盖后续 sync 操作产生的 HID 流量，
    // 防止 monitor 在 ensureBound 矫正后误触发幽灵切换通知
    this.suppressOnboardConfigMonitor(OnboardMonitorSuppress.SyncSequence)
    await this.setOnboardConfig(boundIndex)
    return boundIndex
  }

  /**
   * 板载切换完成后，方案商特有的设备级收尾动作（如 reset 让固件重新加载新槽位）。
   * 默认实现已内联常见方案商逻辑；子类如需自定义可 override。
   *
   * 调用时机：
   * - handleCfgSwitch（板载路径）：setOnboardConfig 之后，不推送本地配置（板载设备自管槽位）。
   * - handleOnboardCfgSync / handleOnboardCfgReplaceSlot：setOnboardConfig 之后、syncCurrentProfileToDevice 之前。
   *
   * 【火柴人板载设备（supportsOnboardConfigs() = true）不走 reset】
   * 原因：syncConfig() 已通过 CMD 0x61 将完整 profile blob 写入当前槽位，
   * 固件读取的就是最新数据，不需要靠 reset 触发固件重新加载槽位。
   * 走 reset 反而会让固件丢弃 0x61 写入的内容，恢复为槽位旧数据。
   * （火柴人非板载模式仍需 reset，故保留下方 !supportsOnboardConfigs() 分支）
   */
  async applyAfterSlotSwitch(): Promise<void> {
    if (Fe87v2DeviceList.includes(deviceManager.deviceID)) {
      await deviceManager.restartAndReconnectCurrentDevice(() => deviceManager.deviceDriver.reset())
    } else if (RoYuanDevice.includes(deviceManager.deviceID)) {
      await deviceManager.deviceDriver.reset()
      await delay(500)
    } else if (He6UltraDeviceList.includes(deviceManager.deviceID) || SlefDevMagnetList.includes(deviceManager.deviceID) || (HuoChaiRenDevices.includes(deviceManager.deviceID) && !deviceManager.deviceDriver.supportsOnboardConfigs())) {
      await deviceManager.deviceDriver.reset()
    }
  }

  protected shouldHandleOnboardConfigSwitch() {
    if (!this.supportsOnboardConfigs() || this.changeProfileFlag) {
      return false
    }

    if (Date.now() < this.onboardConfigMonitorMutedUntil) {
      return false
    }

    return true
  }

  protected async notifyOnboardConfigSwitch(currentIndex: number, onSwitch: (currentIndex: number) => Promise<void> | void) {
    if (!this.shouldHandleOnboardConfigSwitch()) {
      return false
    }

    if (this.onboardConfigLastKnownIndex === currentIndex) {
      return false
    }

    this.onboardConfigLastKnownIndex = currentIndex
    fakeSocket.sendMsgData(DriverSocketMsgType.MSDeviceSwitchSnapshotStart, { Current: currentIndex })
    try {
      await onSwitch(currentIndex)
    } catch (err) {
      // onSwitch 失败时，发送一个 End 消息带当前配置列表，确保前端 loading 被清除
      console.error('notifyOnboardConfigSwitch onSwitch failed:', err)
      const fallbackData = dataProfileManage().CfgList()
      fakeSocket.sendMsgData(DriverSocketMsgType.MSDeviceSwitchSnapshotEnd, fallbackData)
    }
    return true
  }

  protected startOnboardConfigMonitor(onSwitch: (currentIndex: number) => Promise<void> | void) {
    if (this.onboardConfigMonitorDispose) {
      return
    }

    void this.initializeOnboardConfigMonitor()
    this.onboardConfigMonitorDispose = this.DeviceHID.onReciveAny(() => {
      if (!this.shouldHandleOnboardConfigSwitch()) {
        return
      }

      if (this.onboardConfigMonitorTimer) {
        clearTimeout(this.onboardConfigMonitorTimer)
      }

      this.onboardConfigMonitorTimer = setTimeout(() => {
        this.onboardConfigMonitorTimer = null
        void this.handleOnboardConfigMonitor(onSwitch)
      }, 120)
    })
  }

  public suppressOnboardConfigMonitor(ms: number = OnboardMonitorSuppress.Default) {
    // 取较大值：确保已设置的长时间抑制不会被内部的短时间调用（如 setOnboardConfig 内部）覆盖缩短
    const newMutedUntil = Date.now() + ms
    if (newMutedUntil > this.onboardConfigMonitorMutedUntil) {
      this.onboardConfigMonitorMutedUntil = newMutedUntil
    }
  }

  protected rememberOnboardConfigIndex(index: number) {
    this.onboardConfigLastKnownIndex = index
  }

  protected getLastKnownOnboardConfigIndex(): number | null {
    return this.onboardConfigLastKnownIndex
  }

  private async initializeOnboardConfigMonitor() {
    if (!this.supportsOnboardConfigs()) {
      return
    }

    try {
      this.onboardConfigLastKnownIndex = await this.getCurrentOnboardConfigIndex()
    } catch {
      this.onboardConfigLastKnownIndex = null
    }
  }

  private async handleOnboardConfigMonitor(onSwitch: (currentIndex: number) => Promise<void> | void) {
    if (!this.shouldHandleOnboardConfigSwitch()) {
      return
    }

    try {
      const currentIndex = await this.getCurrentOnboardConfigIndex()
      await this.notifyOnboardConfigSwitch(currentIndex, onSwitch)
    } catch {
      return
    }
  }

  protected normalizeFnLayoutStandardKey(
    rawCode: number,
    options?: {
      standardKeyByRawCode?: ReadonlyMap<number, VK>
      allowDirectVkFallback?: boolean
    },
  ): VK | undefined {
    const mappedVk = options?.standardKeyByRawCode?.get(rawCode)
    if (mappedVk !== undefined) {
      return mappedVk
    }

    const vkFromHid = VKFromHID(rawCode)
    if (vkFromHid !== undefined) {
      return vkFromHid
    }

    if (options?.allowDirectVkFallback !== false && HIDFromVk(rawCode as VK) !== undefined) {
      return rawCode as VK
    }

    return undefined
  }

  async getFnKeyMapOverlay(): Promise<Map<KK, number> | undefined> {
    return undefined
  }

  async getFnLayoutOverlay(): Promise<Partial<Layout> | undefined> {
    return undefined
  }

  async getKeyLayoutOverlay(): Promise<Partial<Layout> | undefined> {
    return undefined
  }

  get fnFactoryReset(): FnFactoryResetProvider | undefined {
    return undefined
  }

  clearResetKeys() {
    dataProfileManage().clearResetKeys()
  }

  destroy() {
    if (this.onboardConfigMonitorTimer) {
      clearTimeout(this.onboardConfigMonitorTimer)
      this.onboardConfigMonitorTimer = null
    }
    this.onboardConfigMonitorDispose?.()
    this.onboardConfigMonitorDispose = null
    this.magnet?.destroy()
    // 兼底清理：DeviceProtocol 是单例，统一清空所有 onReciveAny 监听与驱动设置的 responseFilter，避免旧驱动残留。
    this.DeviceHID.clearReciveAnyHandlers()
    this.DeviceHID.clearResponseFilters()
  }
}
